<template>
    <svg ref="root" class="week" viewBox="0 0 440 190" aria-hidden="true" focusable="false">
        <g>
            <rect v-for="day in DAYS" :key="`track-${day.x}`" class="track" :x="day.x" y="8" width="56" height="142" rx="8" />
        </g>
        <g>
            <rect
                v-for="(block, index) in BLOCKS"
                :key="`block-${index}`"
                ref="blockEls"
                class="block"
                :x="block.x"
                :y="block.y"
                width="56"
                :height="block.h"
                rx="5"
                :fill="`var(--color-setup-block-${block.tint})`" />
        </g>
        <rect ref="sweepEl" class="sweep" :x="SWEEP_FROM" y="2" width="3" height="154" rx="1.5" />
        <g>
            <text v-for="day in DAYS" :key="`day-${day.x}`" class="day" :x="day.x + 28" y="174">{{ day.label }}</text>
        </g>
    </svg>
</template>

<script setup lang="ts">
/**
 * Write animation values per frame because throttled tabs left CSS transitions stuck and the loader
 * invisible. Meetings are synthetic because scan results are not available yet.
 */
import { onBeforeUnmount, onMounted, useTemplateRef } from "vue";

const DAYS = [
    { x: 36, label: "M" },
    { x: 114, label: "T" },
    { x: 192, label: "W" },
    { x: 270, label: "T" },
    { x: 348, label: "F" },
];

/** `at` is where in the sweep the playhead reaches this block, as a fraction. */
const BLOCKS = [
    { x: 36, y: 16, h: 36, tint: "violet", at: 0.11 },
    { x: 36, y: 53, h: 26, tint: "teal", at: 0.122 },
    { x: 114, y: 12, h: 40, tint: "pink", at: 0.305 },
    { x: 114, y: 53, h: 34, tint: "violet", at: 0.317 },
    { x: 114, y: 88, h: 26, tint: "amber", at: 0.329 },
    { x: 114, y: 115, h: 32, tint: "teal", at: 0.341 },
    { x: 192, y: 22, h: 32, tint: "green", at: 0.5 },
    { x: 192, y: 70, h: 26, tint: "violet", at: 0.512 },
    { x: 270, y: 16, h: 28, tint: "teal", at: 0.695 },
    { x: 270, y: 45, h: 38, tint: "pink", at: 0.707 },
    { x: 270, y: 84, h: 24, tint: "violet", at: 0.719 },
    { x: 348, y: 34, h: 28, tint: "amber", at: 0.89 },
];

const SWEEP_FROM = 20;
const SWEEP_TO = 420;
const SWEEP_MS = 2200;
const CLEAR_MS = 700;
const LAND_MS = 380;

const blockEls = useTemplateRef<SVGRectElement[]>("blockEls");
const sweepEl = useTemplateRef<SVGRectElement>("sweepEl");

const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;
const easeInOutSine = (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2;
const clamp01 = (t: number): number => Math.min(Math.max(t, 0), 1);

let frame: number | null = null;

function rest(): void {
    sweepEl.value?.style.setProperty("opacity", "0");
    blockEls.value?.forEach((block) => {
        block.style.opacity = "1";
        block.style.transform = "none";
    });
}

function draw(elapsed: number): void {
    const cycle = SWEEP_MS + CLEAR_MS;
    const within = elapsed % cycle;
    const sweeping = within < SWEEP_MS;
    const progress = clamp01(within / SWEEP_MS);

    const sweep = sweepEl.value;
    if (sweep) {
        sweep.setAttribute("x", (SWEEP_FROM + (SWEEP_TO - SWEEP_FROM) * easeInOutSine(progress)).toFixed(2));
        sweep.style.opacity = sweeping ? Math.min(progress * 8, (1 - progress) * 8, 1).toFixed(3) : "0";
    }

    blockEls.value?.forEach((block, index) => {
        const at = BLOCKS[index].at;
        if (!sweeping) {
            const out = clamp01((within - SWEEP_MS) / CLEAR_MS);
            block.style.opacity = (1 - easeOutCubic(clamp01(out * 1.6 - at * 0.6))).toFixed(3);
            return;
        }
        const landed = easeOutCubic(clamp01((within - at * SWEEP_MS) / LAND_MS));
        block.style.opacity = landed.toFixed(3);
        block.style.transform = `scaleY(${(0.72 + 0.28 * landed).toFixed(3)})`;
    });
}

onMounted(() => {
    if (globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        rest();
        return;
    }
    const start = performance.now();
    const tick = (now: number): void => {
        draw(now - start);
        frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
});

onBeforeUnmount(() => {
    if (frame !== null) {
        cancelAnimationFrame(frame);
    }
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .week {
        @apply block w-[440px] max-w-full;
    }

    .track {
        fill: var(--color-surface-sunken);
    }

    /*
     * Avoid multiply blending: blocks never overlap, and blending with the track would alter their token
     * colors.
     */
    .block {
        transform-box: fill-box;
        transform-origin: 50% 0;
    }

    .sweep {
        fill: var(--color-ink);
        filter: drop-shadow(0 0 5px rgb(0 0 0 / 0.22));
    }

    .day {
        @apply scaffold-details-small;
        fill: var(--color-ink-muted);
        text-anchor: middle;
    }
}
</style>
